import React, { useEffect, useRef } from "react";

interface PickerComponentProps {
  hour: number;
  setHour: (hour: number) => void;
  minute: number;
  setMinute: (minute: number) => void;
  minHour?: number;
  maxHour?: number;
  onTimeChange?: (hour: number, minute: number) => void;
  disabledHours?: number[];
  onDisabledHourSelected?: (
    disabledHour: number,
    closestAvailableHour: number,
  ) => void;
}

// Convert decimal time to hour and minute
export const decimalToTime = (
  decimal: number,
): { hour: number; minute: number } => {
  const hour = Math.floor(decimal);
  const minuteDecimal = decimal - hour;
  const minute = Math.round(minuteDecimal * 60);
  return { hour, minute };
};

// Convert hour and minute to decimal time
export const timeToDecimal = (hour: number, minute: number): number => {
  return hour + minute / 60;
};

const DigitalTimePicker: React.FC<PickerComponentProps> = ({
  hour,
  setHour,
  minute,
  setMinute,
  minHour = 0,
  maxHour = 23,
  onTimeChange,
  disabledHours = [],
  onDisabledHourSelected,
}) => {
  const hours = Array.from(
    { length: maxHour - minHour + 1 },
    (_, i) => minHour + i,
  );
  // Only allow 0, 15, 30, 45 minutes
  const minutes = [0, 15, 30, 45];

  const availableHours = hours.filter((h) => !disabledHours.includes(h));

  const findClosestAvailableHour = (targetHour: number): number => {
    if (!disabledHours.includes(targetHour)) {
      return targetHour;
    }

    let closestHour = availableHours[0];
    let minDistance = Math.abs(targetHour - closestHour);

    for (const availableHour of availableHours) {
      const distance = Math.abs(targetHour - availableHour);
      if (distance < minDistance) {
        minDistance = distance;
        closestHour = availableHour;
      }
    }

    return closestHour;
  };

  const itemHeight = 36;
  const visibleItems = 5;
  const pickerHeight = itemHeight * visibleItems;
  const paddingItems = Math.floor(visibleItems / 2);

  const hourScrollRef = useRef<HTMLDivElement>(null);
  const minuteScrollRef = useRef<HTMLDivElement>(null);
  const hourScrollTimer = useRef<NodeJS.Timeout | null>(null);
  const minuteScrollTimer = useRef<NodeJS.Timeout | null>(null);
  const isUserScrolling = useRef(false);

  // Initialize scroll positions
  useEffect(() => {
    const hourIndex = hours.indexOf(hour);
    const minuteIndex = minutes.indexOf(minute);

    setTimeout(() => {
      if (hourIndex >= 0 && hourScrollRef.current) {
        hourScrollRef.current.scrollTop = hourIndex * itemHeight;
      }
      if (minuteIndex >= 0 && minuteScrollRef.current) {
        minuteScrollRef.current.scrollTop = minuteIndex * itemHeight;
      }
    }, 100);
  }, []);

  // Handle external hour changes (from parent)
  useEffect(() => {
    if (!isUserScrolling.current && hourScrollRef.current) {
      const hourIndex = hours.indexOf(hour);
      if (hourIndex >= 0) {
        hourScrollRef.current.scrollTo({
          top: hourIndex * itemHeight,
          behavior: "smooth",
        });
      }
    }
  }, [hour]);

  // Handle external minute changes (from parent)
  useEffect(() => {
    if (!isUserScrolling.current && minuteScrollRef.current) {
      const minuteIndex = minutes.indexOf(minute);
      if (minuteIndex >= 0) {
        minuteScrollRef.current.scrollTo({
          top: minuteIndex * itemHeight,
          behavior: "smooth",
        });
      }
    }
  }, [minute]);

  const handleHourScroll = (event: React.UIEvent<HTMLDivElement>) => {
    isUserScrolling.current = true;
    const y = event.currentTarget.scrollTop;
    const index = Math.round(y / itemHeight);
    const clampedIndex = Math.max(0, Math.min(hours.length - 1, index));
    const selectedHour = hours[clampedIndex];

    if (selectedHour !== undefined && selectedHour !== hour) {
      setHour(selectedHour);
      onTimeChange?.(selectedHour, minute);
    }

    if (hourScrollTimer.current) {
      clearTimeout(hourScrollTimer.current);
    }

    hourScrollTimer.current = setTimeout(() => {
      const snapY = clampedIndex * itemHeight;
      if (hourScrollRef.current) {
        hourScrollRef.current.scrollTo({
          top: snapY,
          behavior: "smooth",
        });
      }

      if (disabledHours.includes(selectedHour)) {
        const closestAvailableHour = findClosestAvailableHour(selectedHour);

        if (onDisabledHourSelected) {
          onDisabledHourSelected(selectedHour, closestAvailableHour);
        }

        setHour(closestAvailableHour);
        onTimeChange?.(closestAvailableHour, minute);

        const closestHourIndex = hours.indexOf(closestAvailableHour);
        if (closestHourIndex >= 0 && hourScrollRef.current) {
          hourScrollRef.current.scrollTo({
            top: closestHourIndex * itemHeight,
            behavior: "smooth",
          });
        }
      }

      isUserScrolling.current = false;
    }, 150);
  };

  const handleMinuteScroll = (event: React.UIEvent<HTMLDivElement>) => {
    isUserScrolling.current = true;
    const y = event.currentTarget.scrollTop;
    const index = Math.round(y / itemHeight);
    const clampedIndex = Math.max(0, Math.min(minutes.length - 1, index));
    const newMinute = minutes[clampedIndex];

    if (newMinute !== undefined && newMinute !== minute) {
      setMinute(newMinute);
      onTimeChange?.(hour, newMinute);
    }

    if (minuteScrollTimer.current) {
      clearTimeout(minuteScrollTimer.current);
    }

    minuteScrollTimer.current = setTimeout(() => {
      const snapY = clampedIndex * itemHeight;
      if (minuteScrollRef.current) {
        minuteScrollRef.current.scrollTo({
          top: snapY,
          behavior: "smooth",
        });
      }
      isUserScrolling.current = false;
    }, 150);
  };

  const paddedHours = [
    ...Array(paddingItems).fill(null),
    ...hours,
    ...Array(paddingItems).fill(null),
  ];

  const paddedMinutes = [
    ...Array(paddingItems).fill(null),
    ...minutes,
    ...Array(paddingItems).fill(null),
  ];

  if (availableHours.length === 0) {
    return (
      <div className="digital-time-picker-empty">
        <span className="empty-text">Not Available</span>
      </div>
    );
  }

  return (
    <div className="digital-time-picker-container">
      <div className="picker-row">
        <div className="picker-column" style={{ height: `${pickerHeight}px` }}>
          <div
            ref={hourScrollRef}
            className="picker-scroll"
            onScroll={handleHourScroll}
          >
            {paddedHours.map((h, index) => {
              const isDisabled = h !== null && disabledHours.includes(h);
              return (
                <div
                  key={`hour-${index}`}
                  className="picker-item"
                  style={{ height: `${itemHeight}px` }}
                >
                  {h !== null && (
                    <span
                      className={`picker-item-text ${h === hour ? "selected" : ""} ${isDisabled ? "disabled" : ""}`}
                    >
                      {h.toString().padStart(2, "0")}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <span className="picker-separator">:</span>

        <div className="picker-column" style={{ height: `${pickerHeight}px` }}>
          <div
            ref={minuteScrollRef}
            className="picker-scroll"
            onScroll={handleMinuteScroll}
          >
            {paddedMinutes.map((m, index) => (
              <div
                key={`minute-${index}`}
                className="picker-item"
                style={{ height: `${itemHeight}px` }}
              >
                {m !== null && (
                  <span
                    className={`picker-item-text ${m === minute ? "selected" : ""}`}
                  >
                    {m.toString().padStart(2, "0")}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="picker-selection" />
    </div>
  );
};

export default DigitalTimePicker;
